'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMarketplaceStore } from '@/lib/marketplace/store';
import type { MarketplaceListing } from '@/lib/marketplace/types';
import { Download, CheckCircle2, Link2, AlertTriangle } from 'lucide-react';

interface InstallDialogProps {
  listing: MarketplaceListing;
  open: boolean;
  onClose: () => void;
}

export function InstallDialog({ listing, open, onClose }: InstallDialogProps) {
  const { install, installsLoading } = useMarketplaceStore();

  const [autoUpdate, setAutoUpdate] = useState(false);
  const [nameOverride, setNameOverride] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInstall = async () => {
    setError(null);
    try {
      await install(listing.id, {
        auto_update: autoUpdate,
        name_override: nameOverride || undefined,
      });
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    setNameOverride('');
    setAutoUpdate(false);
    onClose();
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="size-5" />
              Installation Complete
            </DialogTitle>
            <DialogDescription>
              {listing.name} has been installed successfully.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              You can now use this {listing.listing_type.replace('_', ' ')} in your workspace.
              {listing.listing_type === 'agent_template' &&
                ' A new agent has been created from this template.'}
            </p>
          </div>

          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="size-5" />
            Install {listing.name}
          </DialogTitle>
          <DialogDescription>
            Review the installation details before proceeding.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          {/* Listing Info */}
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            {listing.icon_url ? (
              <img
                src={listing.icon_url}
                alt={listing.name}
                className="size-12 rounded-lg object-cover"
              />
            ) : (
              <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                {listing.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h4 className="font-medium">{listing.name}</h4>
              <p className="text-sm text-muted-foreground">v{listing.version}</p>
            </div>
            <Badge variant={listing.price_type === 'free' ? 'outline' : 'default'}>
              {listing.price_type === 'free'
                ? 'Free'
                : `$${(listing.price_cents / 100).toFixed(2)}`}
            </Badge>
          </div>

          {/* Required Connections */}
          {listing.required_connections && listing.required_connections.length > 0 && (
            <div className="space-y-2">
              <Label>Required Connections</Label>
              <div className="flex flex-wrap gap-2">
                {listing.required_connections.map((conn) => (
                  <Badge key={conn} variant="secondary" className="gap-1">
                    <Link2 className="size-3" />
                    {conn}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                You'll need to set up these connections to use this item.
              </p>
            </div>
          )}

          {/* Name Override (for agents) */}
          {listing.listing_type === 'agent_template' && (
            <div className="space-y-2">
              <Label htmlFor="name-override">Agent Name (optional)</Label>
              <Input
                id="name-override"
                value={nameOverride}
                onChange={(e) => setNameOverride(e.target.value)}
                placeholder={listing.name}
              />
              <p className="text-sm text-muted-foreground">
                Leave blank to use the template name.
              </p>
            </div>
          )}

          {/* Auto Update */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto-update">Auto Update</Label>
              <p className="text-sm text-muted-foreground">
                Automatically install new versions
              </p>
            </div>
            <Switch
              id="auto-update"
              checked={autoUpdate}
              onCheckedChange={setAutoUpdate}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleInstall} disabled={installsLoading}>
            {installsLoading ? (
              'Installing...'
            ) : (
              <>
                <Download className="size-4 mr-2" />
                Install
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
